import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { CartModal } from '@/components/CartModal';
import { NotificationDropdown } from '@/components/notification-dropdown/NotificationDropdown';
import { SettingsModal } from '@/components/SettingsModal';
import { PaymentProcessingModal } from '@/components/PaymentProcessingModal';
import { PaymentErrorModal } from '@/components/PaymentErrorModal';
import { TaxInvoiceModal } from '@/components/TaxInvoiceModal';
import { InvoiceCompleteModal } from '@/components/InvoiceCompleteModal';
import { StoreMapModal } from '@/components/StoreMapModal';
import { LogoutConfirmModal } from '@/components/LogoutConfirmModal';
import { Toaster } from '@/components/ui/sonner';
import { ROUTES } from '@/config/routes';
import { hasViewModeQuery } from '@/lib/app-utils';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useCart } from '@/contexts/CartContext';
import { usePaymentFlow, useInvoice } from '@/contexts/CheckoutContext';
import { useNotifications } from '@/contexts/NotificationsContext';

interface StoreInfo {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  isOpen: boolean;
  distance: string;
  rating: number;
  imageUrl: string;
}

/**
 * AppModals Props
 * 이벤트 기반으로 리팩토링하여 모달 표시 상태는 내부에서 관리
 * Cart/Notifications/Payment/Invoice 상태는 Context에서 직접 가져옴
 */
interface AppModalsProps {
  isTableOrderMode: boolean;
  onCheckout: () => void;
  onTableOrderComplete: () => void;
  stores: StoreInfo[];
  onStoreSelect: (storeId: string) => void;
  onConfirmLogout: () => void;
}

/**
 * 모달 이벤트 타입
 */
type ModalEventName =
  | 'openSettingsModal'
  | 'openMapModal'
  | 'openLogoutConfirm'
  | 'toggleCart'
  | 'closeCart'
  | 'toggleNotifications'
  | 'closeNotifications';

export function AppModals({
  isTableOrderMode,
  onCheckout,
  onTableOrderComplete,
  stores,
  onStoreSelect,
  onConfirmLogout,
}: AppModalsProps) {
  // Context에서 직접 상태와 핸들러 가져오기
  const {
    cartItems,
    updateQuantity,
    removeFromCart,
    clearCart,
    updateCartItem,
  } = useCart();

  const {
    notifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
  } = useNotifications();

  const {
    paymentFlow,
    handleProcessingError,
    selectedCardForPayment,
    handlePaymentFlowReset,
    handleRetryPayment,
    handleAddNewCard,
    orderData,
  } = usePaymentFlow();

  const {
    invoiceData,
    selectedCompany,
    handleTaxInvoiceComplete,
    handleInvoiceClose,
    handleCompleteClose,
  } = useInvoice();

  // URL 라우팅 훅
  const location = useLocation();
  const { navigate, goBack } = useSafeNavigate();

  // URL 기반 장바구니 상태.
  // #434 codex R2 — `/cart?mode=view` 직접 진입 차단 (/checkout 게이트와 대칭).
  // 일반 `/cart` 직접 진입은 차단하지 않는다: 카트 아이템은 주문 가능 컨텍스트에서만
  // 담기므로(view 모드는 담기 UI 자체가 미렌더) 그 주문은 합법 주문이다.
  const isViewModeQuery = hasViewModeQuery(location.search);
  const showCart = location.pathname === ROUTES.CART && !isViewModeQuery;

  // 내부 모달 상태 관리 (URL로 관리하지 않는 모달)
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // 장바구니 닫기 (URL 네비게이션)
  const closeCart = useCallback(() => goBack(ROUTES.HOME), [goBack]);

  // 모달 닫기 핸들러
  const closeNotifications = useCallback(() => setShowNotifications(false), []);
  const closeSettingsModal = useCallback(() => setShowSettingsModal(false), []);
  const closeMapModal = useCallback(() => setShowMapModal(false), []);
  const closeLogoutConfirm = useCallback(() => setShowLogoutConfirm(false), []);

  // 로그아웃 확인 핸들러
  const confirmLogout = useCallback(() => {
    setShowLogoutConfirm(false);
    onConfirmLogout();
  }, [onConfirmLogout]);

  // 매장 선택 핸들러
  const handleStoreSelectClick = useCallback((storeId: string) => {
    setShowMapModal(false);
    onStoreSelect(storeId);
  }, [onStoreSelect]);

  // 주문 총액 계산
  const orderTotal = (orderData?.total as number) || 0;

  // 체크아웃 핸들러 (체크아웃 페이지로 이동)
  const handleCheckoutClick = useCallback(() => {
    onCheckout();
  }, [onCheckout]);

  // 테이블 주문 완료 핸들러
  const handleTableOrderCompleteClick = useCallback(() => {
    goBack(ROUTES.HOME); // 장바구니 닫기 (URL 기반)
    onTableOrderComplete();
  }, [goBack, onTableOrderComplete]);

  // 이벤트 리스너 설정
  useEffect(() => {
    // 장바구니 토글 (URL 기반)
    const handleToggleCart = () => {
      if (showCart) {
        goBack(ROUTES.HOME);
      } else {
        navigate(ROUTES.CART);
      }
    };

    const eventHandlers: Record<ModalEventName, () => void> = {
      openSettingsModal: () => setShowSettingsModal(true),
      openMapModal: () => setShowMapModal(true),
      openLogoutConfirm: () => setShowLogoutConfirm(true),
      toggleCart: handleToggleCart,
      closeCart: () => goBack(ROUTES.HOME),
      toggleNotifications: () => setShowNotifications(prev => !prev),
      closeNotifications: () => setShowNotifications(false),
    };

    // 모든 이벤트 리스너 등록
    Object.entries(eventHandlers).forEach(([eventName, handler]) => {
      window.addEventListener(eventName, handler);
    });

    return () => {
      // 정리
      Object.entries(eventHandlers).forEach(([eventName, handler]) => {
        window.removeEventListener(eventName, handler);
      });
    };
  }, [navigate, goBack, showCart]);

  return (
    <>
      <CartModal
        items={cartItems}
        isOpen={showCart}
        onClose={closeCart}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeFromCart}
        onClearCart={clearCart}
        onCheckout={handleCheckoutClick}
        onUpdateCartItem={updateCartItem}
        mode={isTableOrderMode ? 'table' : 'pickup'}
        onTableOrderComplete={handleTableOrderCompleteClick}
      />

      <NotificationDropdown
        notifications={notifications}
        isOpen={showNotifications}
        onClose={closeNotifications}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
        onDeleteNotification={deleteNotification}
        onDeleteAll={deleteAllNotifications}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={closeSettingsModal}
      />

      {/* #687: onSuccess 제거 — 클라이언트 모의 처리로 결제 성공 선언 불가 (fail-closed) */}
      <PaymentProcessingModal
        isOpen={paymentFlow === 'processing'}
        onError={handleProcessingError}
        amount={orderTotal}
        method={
          orderData?.paymentMethod === 'cash'
            ? 'cash'
            : orderData?.paymentMethod === 'transfer'
            ? 'transfer'
            : 'card'
        }
        cardInfo={
          orderData?.paymentMethod === 'cash' || orderData?.paymentMethod === 'transfer'
            ? undefined
            : { type: selectedCardForPayment || 'visa', lastFour: '1234', holder: 'John Doe' }
        }
      />

      <PaymentErrorModal
        isOpen={paymentFlow === 'error'}
        onClose={handlePaymentFlowReset}
        onRetry={handleRetryPayment}
        onAddNewCard={handleAddNewCard}
        savedCards={[]} // #69: 목업 데이터 제거
      />

      {orderData && orderData.orderId && orderData.total !== undefined && orderData.timestamp && (
        <TaxInvoiceModal
          isOpen={paymentFlow === 'invoice'}
          onClose={handleInvoiceClose}
          onComplete={handleTaxInvoiceComplete}
          orderData={{
            orderId: orderData.orderId,
            total: orderData.total,
            timestamp: orderData.timestamp,
            // #285 stale 방지: 주문 시점 세율·통화 스냅샷 전달
            taxRate: orderData.taxRate,
            currency: orderData.currency,
          }}
          selectedCompany={selectedCompany}
        />
      )}

      {invoiceData && (
        <InvoiceCompleteModal
          isOpen={paymentFlow === 'complete'}
          onClose={handleCompleteClose}
          invoiceData={invoiceData}
        />
      )}

      <StoreMapModal
        isOpen={showMapModal}
        onClose={closeMapModal}
        stores={stores}
        onStoreSelect={handleStoreSelectClick}
      />

      <LogoutConfirmModal
        isOpen={showLogoutConfirm}
        onClose={closeLogoutConfirm}
        onConfirm={confirmLogout}
      />

      <Toaster
        position="top-center"
        richColors
        closeButton
        toastOptions={{ style: { fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif" } }}
      />
    </>
  );
}
